<template>
	<div class="p-field p-col-2">
		<label for="name">{{ $t('Squeeze.System.ButtonName') }} *</label>
		<InputText id="name" v-model="value.buttonName" disabled />
	</div>
	<div class="p-field p-col-3">
		<label for="description">{{ $t('Squeeze.System.Description') }}</label>
		<InputText id="description" v-model="value.description" :class="{'p-invalid':v$.description.maxLengthOfFifteenValidation.$invalid && showErrorMessage}" />
		<small v-if="v$.description.maxLengthOfFifteenValidation.$invalid && showErrorMessage" class="p-error">
			{{ $t('Forms.Val.ButtonDescription') }}
		</small>
	</div>
	<div class="p-field p-col-3">
		<label for="color">{{ $t('Squeeze.System.Color') }}</label>
		<InputGroup>
			<InputGroupAddon class="p-p-0">
				<input ref="colorInput" type="color" :value="value.color" @change="onChangeColor($event.target.value)" class="input-color">
			</InputGroupAddon>
			<InputText id="color" v-model="value.color" @click="this.$refs.colorInput.click();" readonly />
		</InputGroup>
	</div>
	<div class="p-field p-col-3">
		<div>
			<label for="icon">{{ $t('Squeeze.System.Icon') }}</label>
			<Badge class="mdi mdi-comment-question-outline" v-tooltip.right="$t('Squeeze.System.GoToMaterialDesignIcons')" @click="goToMaterialDesignIcons" />
		</div>
		<InputText id="icon" v-model="value.icon" :class="{'p-invalid':(v$.icon.isIconFormatCorrect.$invalid || v$.icon.whitespaceValidation.$invalid) && showErrorMessage}" />
		<small v-if="v$.icon.isIconFormatCorrect.$invalid && showErrorMessage" class="p-error">
			{{ $t('Forms.Val.ButtonIcon') }}
		</small>
		<small v-if="v$.icon.whitespaceValidation.$invalid && showErrorMessage" class="p-error">
			{{ $t('Forms.Val.NoWhitespace') }}
		</small>
	</div>
	<div class="p-field p-col-1 p-d-flex p-ai-start p-mt-4">
		<Button
			v-tooltip.top="$t('Squeeze.General.Save')"
			type="button"
			icon="mdi mdi-content-save-outline"
			class="p-button p-mr-1"
			@click="saveValidationButtonSettings"
		/>
		<Button
			v-tooltip.top="$t('Squeeze.System.ResetValidationButtonSettings')"
			type="button"
			icon="mdi mdi-delete-outline"
			class="p-button p-button-danger"
			@click="resetValidationButtonSettings"
		/>
	</div>
</template>

<script lang="ts">
import {defineComponent, PropType, reactive, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useToast} from "primevue/usetoast";
import Badge from 'primevue/badge';
import InputText from "primevue/inputtext";
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import {useVuelidate} from "@vuelidate/core";
import {
	isIconFormatCorrect,
	maxLengthOfFifteenValidation,
	whitespaceValidation
} from "@/util/ValidationHelper";
import {ValidationButtonSettingsDto} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import Tooltip from "primevue/tooltip";
import ButtonNameEnum = ValidationButtonSettingsDto.ButtonNameEnum;

export default defineComponent({
	name: "SystemValidationButtonSettingsForm",
	methods: {isIconFormatCorrect, maxLengthOfFifteenValidation},
	components: {
		Badge,
		InputText,
		InputGroup,
		InputGroupAddon,
	},
	directives: {
		'tooltip': Tooltip,
	},
	props: {
		setting: {
			type: Object as PropType<ValidationButtonSettingsDto>,
			default: () => ({}),
		},
	},
	emits: ['load'],
	setup(props, { emit }) {
		const {t} = useI18n();
		const toast = useToast();

		/** Api for validation */
		const validationApi = ClientManager.getInstance().squeeze.validation;

		/** Show Loading on load data */
		const loading = ref(false);

		/** Triggered when (all) field values are invalid */
		const showErrorMessage = ref<boolean>(false);

		/** Triggered the valid of form */
		const isInvalid = ref<boolean>(true);

		/** Current settings of button settings */
		const value = reactive<ValidationButtonSettingsDto>({
			buttonName: props.setting?.buttonName || ButtonNameEnum.Validate,
			description: props.setting?.description || '',
			color: props.setting?.color || '',
			icon: props.setting?.icon || '',
		});

		/** Determines the required rules for validation */
		const rules = {
			description: { maxLengthOfFifteenValidation },
			color: { },
			icon: { isIconFormatCorrect, whitespaceValidation },
		};

		/** Use Vuelidate */
		const v$ = useVuelidate(rules as any, value);

		/** Save the validation button settings */
		const saveValidationButtonSettings = () => {
			if (v$.value.$invalid) {
				showErrorMessage.value = true;
				return
			}

			showErrorMessage.value = false;
			loading.value = true;

			validationApi.putValidationButtonSettingsByName(value.buttonName as any, value)
				.then(() => {
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.System.SaveValidationButtonSettingsMessage'));
				})
				.catch(response => response.json().then((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				})
		}

		/** Reset the validation button settings by button name */
		const resetValidationButtonSettings = () => {
			loading.value = true;
			validationApi.deleteValidationButtonSettings(value.buttonName as any)
				.then(() => {
					value.description = '';
					value.color = '';
					value.icon = '';
				})
				.catch(response => response.json().then((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				})
		}

		/**
		 * Triggered when color changed
		 * @param color
		 */
		const onChangeColor = (color: string) => {
			value.color = color;
		}

		/** Go to the material design icons page, when badge clicked */
		const goToMaterialDesignIcons = () => {
			window.open('https://pictogrammers.github.io/@mdi/font/5.4.55/');
		}

		/** Watch loading property */
		watch(() => loading.value, () => {
			emit('load', loading.value);
		})

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(() => props.setting, (newVal) => {
			if (newVal) {
				Object.assign(value, newVal);
			}
		}, { immediate: true, deep: true })

		return {
			t,
			toast,
			loading,
			showErrorMessage,
			isInvalid,
			value,
			rules,
			v$,
			saveValidationButtonSettings,
			resetValidationButtonSettings,
			onChangeColor,
			goToMaterialDesignIcons,
		}
	},
})
</script>

<style scoped>

::v-deep(.p-inputtext) {
	height: 2.678rem;
}

::v-deep(.p-badge) {
	background-color: transparent;
	max-width: 1.5rem;
	order: 2;
	cursor: pointer;
}

span.p-badge.p-component.p-badge-dot.mdi.mdi-comment-question-outline {
	color: var(--text-color);
	cursor: pointer;
}

.input-color {
	width: 2.478rem;
	height: 2.478rem;
	cursor: pointer;
	background-color: transparent;
	border: none;
	padding: 0;
}

.input-color::-webkit-color-swatch {
	border-radius: 0.25rem;
	border: none;
}


</style>
